import { useEffect, useState } from 'react'

type Breakpoint = {
	width: number
	count: number
}

type UseVisibleItemsOptions<T> = {
	items: T[]
	breakpoints: Breakpoint[]
}

/**
 * Хук для управления видимостью элементов в зависимости от ширины экрана
 *
 * @param items - массив элементов для отображения
 * @param breakpoints - массив брейкпоинтов с количеством видимых элементов
 * @returns объект с видимыми и скрытыми элементами
 *
 * @example
 * const { visibleItems, hiddenItems } = useVisibleItems({
 *   items: myItems,
 *   breakpoints: [
 *     { width: 1100, count: 9 },
 *     { width: 1200, count: 10 },
 *     { width: 1400, count: 11 }
 *   ]
 * })
 */
export const useVisibleItems = <T>({ items, breakpoints }: UseVisibleItemsOptions<T>) => {
	const [visibleItemsCount, setVisibleItemsCount] = useState(items.length)
	const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window?.innerWidth : null)

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth)
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	useEffect(() => {
		if (!windowWidth) {
			setVisibleItemsCount(items.length)
			return
		}

		// Сортируем брейкпоинты по возрастанию ширины
		const sortedBreakpoints = [...breakpoints].sort((a, b) => a.width - b.width)

		// Находим подходящий брейкпоинт
		let count = items.length
		for (const breakpoint of sortedBreakpoints) {
			if (windowWidth < breakpoint.width) {
				count = breakpoint.count
				break
			}
		}

		setVisibleItemsCount(count)
	}, [windowWidth, items.length, breakpoints])

	return {
		visibleItems: items.slice(0, visibleItemsCount),
		hiddenItems: items.slice(visibleItemsCount),
		visibleItemsCount,
		windowWidth
	}
}
